import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { editAgentValidationSchema } from "validations/agent.validation";

import { AddNewOrUpdateAgentAsync } from "services/employeeManagement.service";

import {
  IAgentAuthListItem,
  IAddNewAgentForm,
  IAgent,
} from "models/Employee/employeeManagement.model";
import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import {
  Button,
  Panel,
  PanelHeader,
  Spinner,
  TextField,
  Typography,
} from "components";
import Alert from "components/BaseComponents/Alert/Alert";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { TokenInclude } from "helpers/GetToken";
import { eachFirstLetterCapital } from "utils/textfield.utils";
import { Mapper } from "helpers/AuthListMapper";
import { AuthListItem } from "../common/AuthListItem";

const EditAgent: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const { name, phoneNumber } = useParams();

  const refScrollEdit = useRef<HTMLHeadElement | null>(null);

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isEditChange, setIsEditChange] = useState<boolean>(true);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(
    t("success_update_message").toString()
  );

  const [isAuthCheck, setIsAuthCheck] = useState<boolean>(false);
  const [agentAuthList, setAgentAuthList] = useState<IAgentAuthListItem[]>([]);
  const token = TokenInclude();

  const { agents } = useAppSelector((state) => state.employeeManagement);

  const onAuthChange = (isToggle?: boolean): void => {
    if (isToggle) setIsAuthCheck(isToggle);
  };

  const defaultFormValues: IAddNewAgentForm = {
    name: `${name}`,
    mobilNo: `${phoneNumber}`,
  };
  const {
    control,
    handleSubmit,
    errors,
    formState: { isSubmitting },
  } = useForm<IAddNewAgentForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(editAgentValidationSchema(t)),
  });

  const formSubmit = async (data: any): Promise<void> => {
    const mobilNo = data.mobilNo;
    const name = eachFirstLetterCapital(data.name);
    delete data.name;
    delete data.mobilNo;

    const authListMapper: IAgentAuthListItem[] = Mapper(data, agentAuthList);

    const retVal = await AddNewOrUpdateAgentAsync(
      token,
      authListMapper,
      mobilNo,
      name,
      true
    );
    if (retVal.Issucceeded) {
      setIsAddSuccess(true);
      setIsEditChange(false);
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
    }

    if (refScrollEdit?.current) {
      setTimeout(function () {
        refScrollEdit.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  useEffect(() => {
    if (agents?.AgentList && agents?.AgentList?.length > 0) {
      const singleAgent = agents?.AgentList.find((f: IAgent) =>
        f.MobileNo.includes(phoneNumber!)
      );
      if (singleAgent?.AuthList) setAgentAuthList(singleAgent.AuthList);
    }
  }, [agents, phoneNumber]);

  return (
    <ElementWrapper
      width='872px'
      height={isMobile ? "" : "auto"}
      minHeight={isMobile ? "" : "calc(100vh + 305px)"}
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
    >
      {(isAddSuccess || isAddError) && (
        <Alert
          alertType={isAddSuccess ? "Success" : "Error"}
          duration={6000}
          isVisible={isAddSuccess || isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <Spinner show={isSubmitting} />
      <section
        ref={refScrollEdit}
        id='editAgent-section'
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          scrollBehavior: "smooth",
        }}
      />
      <Panel width='100%' marginTop='30px'>
        <form onSubmit={handleSubmit(formSubmit)} noValidate>
          <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
            <Typography
              margin='10px 0'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("edit_agent")}
            </Typography>
          </Row>
          <PanelHeader />
          <StyledFormWrapper>
            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.name?.message}
                  label={t("just_emp_name").toString()}
                  maxLength={100}
                  name='name'
                  placeholder={t("name_surname_validation").toString()}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextField
                  control={control}
                  error={errors.mobilNo?.message}
                  label={t("phone_number").toString()}
                  maxLength={15}
                  name='mobilNo'
                  type='phoneNumber'
                  placeholder='(5**) *** ** **'
                  disabled={true}
                />
              </Col>
            </Row>
          </StyledFormWrapper>

          <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
            <Typography
              margin='10px 0'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("agent_add_auth_list")}
            </Typography>
          </Row>
          <PanelHeader />
          <StyledFormWrapper>
            {AuthListItem({
              control,
              agentAuthList,
              isAuthCheck,
              onAuthChange,
              isDetail: false,
            })}
          </StyledFormWrapper>
          <Row
            backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
            justifyContent='space-between'
            isResponsive
            noMargin
            gap
          >
            <Col>
              <Button
                type='button'
                width={isMobile ? "100%" : "100px"}
                text={`← ${t("back")}`}
                onClick={() =>
                  navigate("/employee-panel", {
                    state: { fromAddOrEditAgent: isEditChange },
                  })
                }
                color='light'
              />
            </Col>
            <Col maxWidth='130px'>
              <Button text={t("update_button").toString()} type='submit' />
            </Col>
          </Row>
        </form>
      </Panel>
    </ElementWrapper>
  );
};

export default EditAgent;
